import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { ax } from "src/common/api/apiClient";
import { IApi } from "src/common/typings/app";
import {
  INewUser,
  INewUserGroup,
  IPortalAlertMessage,
  ISecurityKey,
  IUpdateUser,
  IUpdateUserRole,
} from "src/common/typings/user";
import { ManagementState } from "../managementSlice";

export const handleAddUser = (state: ManagementState, { payload }: PayloadAction<any>) => {
  state.status = "idle";
  state.newUsers.push(payload);
};

export const handleAddFail = (state: ManagementState) => {
  state.status = "failed";
};

export const addUserAction = createAsyncThunk(
  "management/addUserAction",
  async (user: INewUser, { dispatch }) => {
    try {
      const withNumericPhone: INewUser = {
        ...user,
        phoneNumber: user.phoneNumber.replace(/\D/g, ""),
        mobileNumber: user.mobileNumber?.replace(/\D/g, ""),
      };
      const res = await ax.post<IApi>("/create-user", withNumericPhone, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log(res);
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "User added Successfully!",
            options: { variant: "success" },
          })
        );
        return user;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const handleUpdateUser = (state: ManagementState, { payload }: PayloadAction<any>) => {
  state.status = "idle";
  state.newUsers.push(payload);
};

export const handleUpdateFail = (state: ManagementState) => {
  state.status = "failed";
};

export const updateUserAction = createAsyncThunk(
  "management/updateUserAction",
  async (user: IUpdateUser, { dispatch }) => {
    try {
      const res = await ax.post<IApi>("/update-user", user, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log(res);
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "User updated successfully!",
            options: { variant: "success" },
          })
        );
        return user;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const handleGetUser = (state: ManagementState, { payload }: PayloadAction<IUpdateUser>) => {
  state.status = "idle";
  state.currentUser = payload;
};

export const handleGetFail = (state: ManagementState) => {
  state.status = "failed";
};

export const getUserAction = createAsyncThunk(
  "management/getUserAction",
  async ({ userId }: { userId: string }): Promise<IUpdateUser> => {
    try {
      const res = await ax.get<IApi & { user: IUpdateUser }>(`/get-user-by-user-id/${userId}`);
      console.log(res);
      if (res.status === 200 && res.data.success) return res.data.user;
      else throw new Error(res.data.error || res.statusText);
    } catch (error) {
      throw error;
    }
  }
);

export const handleDeleteFail = (state: ManagementState) => {
  state.status = "failed";
};

export const deleteUserAction = createAsyncThunk(
  "management/deleteUserAction",
  async ({ userId }: { userId: number }, { dispatch }): Promise<IApi> => {
    try {
      const res = await ax.get<IApi>(`/delete-user-by-user-id/${userId}`);
      console.log(res);
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "User deleted successfully!",
            options: { variant: "success" },
          })
        );
        return res.data;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const getRolesAction = createAsyncThunk("management/getRolesAction", async () => {
  try {
    const res = await ax.get<IApi & { roles: string[] }>("/get-roles");
    if (res.status === 200 && res.data.success) return res.data.roles;
    else throw new Error("Unable to get roles from API");
  } catch (error) {
    throw error;
  }
});

export const resetPasswordAction = createAsyncThunk(
  "management/resetPasswordAction",
  async (user: any, { dispatch }) => {
    try {
      const res = await ax.get<IApi>(`/reset-user-password-by-user-id/${user.userId}`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      console.log(res);
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "Password reset request has been processed!",
            options: { variant: "success" },
          })
        );
        return user;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const getSecurityKeys = createAsyncThunk("management/getSecurityKeys", async (): Promise<
  ISecurityKey[]
> => {
  try {
    const res = await ax.get<IApi & { securityKeys: ISecurityKey[] }>("/get-security-keys");
    if (res.status === 200 && res.data.success)
      if (res.data.securityKeys && res.data.securityKeys.length) return res.data.securityKeys;
      else return [];
    else throw new Error(res.data.error || res.statusText);
  } catch (error) {
    throw error;
  }
});
export const getCustomAlertData = createAsyncThunk(
  "management/getCustomAlertData",
  async (): Promise<IPortalAlertMessage> => {
    try {
      const res = await ax.get<IApi & IPortalAlertMessage>("/get-vendor-disclaimer-values");
      if (res.status === 200 && res.data.success) return res.data;
      else throw new Error(res.data.error || res.statusText);
    } catch (error) {
      throw error;
    }
  }
);
export const addUserRoleAction = createAsyncThunk(
  "management/addUserRoleAction",
  async (userGroup: INewUserGroup, { dispatch }) => {
    try {
      const res = await ax.post<IApi>("/create-role", userGroup, {
        headers: {
          // "Content-Type": "application/json",
          "Content-Type": "application/json",
        },
      });
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "User Group Added!",
            options: { variant: "success" },
          })
        );
        return userGroup;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);

export const handleGetRoleByName = (
  state: ManagementState,
  { payload }: PayloadAction<IUpdateUser>
) => {
  state.status = "idle";
  state.currentUser = payload;
};

export const handleGetRoleByNameFail = (state: ManagementState) => {
  state.status = "failed";
};

export const getRoleByNameAction = createAsyncThunk(
  "management/getRoleByNameAction",
  async (roleName: string): Promise<any> => {
    try {
      const res = await ax.get<IApi & IUpdateUserRole>(
        `/get-role-by-role-name/${encodeURI(roleName)}`
      );
      if (res.status === 200 && res.data.success) {
        // setUserGroupId(res.data.roleId);
        // const securityKeys = res.data.securityKeys?.join(",").split(",");
        // setUserRoleSecurityKeys(securityKeys);
        return res.data;
      } else throw new Error(res.data.error || res.statusText);
    } catch (error) {
      throw error;
    }
  }
);

// export const handleUpdateRole = (state: ManagementState, { payload }: PayloadAction<any>) => {
//   console.log("role updated successfully");
//   state.status = "idle";
//   state.currentUser = payload;
//   state.errorMessage = "User Group updated successfully!";
// };

export const updateUserRoleAction = createAsyncThunk(
  "management/updateUserRoleAction",
  async (userRole: IUpdateUserRole, { dispatch }) => {
    try {
      const res = await ax.post<IApi>("/update-role", userRole, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (res.status === 200 && res.data.success) {
        dispatch(
          addNotificationAction({
            message: "User Group Updated!",
            options: { variant: "success" },
          })
        );
        return res;
      } else {
        throw new Error(res.data.error || res.statusText);
      }
    } catch (error) {
      dispatch(
        addNotificationAction({
          message: error instanceof Error ? error.message : "An error occurred",
          options: { variant: "error" },
        })
      );
      throw error;
    }
  }
);
